import { defaultFetchHeaders, fetchJson } from "../../../utils";
import { validateExpenseUploadFile } from "../../expense/types";
import type {
  Expense,
  ExpenseProject,
  ExpenseProjectOption,
  ExpenseProjectSummary,
  ExpenseAttachment,
  ExpenseCategory,
  ExpenseCategoryReport,
  ExpenseExtraction,
  ExpenseConfirmationResult,
  DuplicateExpenseWarning,
  ExpenseListResponse,
  ExpenseImportBatch,
  ExpenseImportCandidate,
  ExpenseImportSourceFile,
  ExpenseMerchantReportPage,
  ExpenseMerchantDetails,
  MerchantAddress,
  ExpensePeriodReport,
  ExpensePaymentAccountReport,
  ExpenseSchedule,
  ExpenseScheduleBackfillPreview,
  ExpenseScheduleBackfillResult,
  ExpenseScheduleRateComponentType,
  ExpenseScheduleRoundingMode,
  ExpenseStatus,
  ExpenseSummary,
  ExpenseShipment,
  ExpenseShipmentCarrier,
  PaymentAccount,
  PaymentAccountType,
  ReceiptImport,
  ReceiptReview,
} from "../../expense/types";

const base = `${process.env.REACT_APP_BACKEND_URL || ""}/expense`;

const listExpenses = (
  params: ExpenseListParams = {},
  signal?: AbortSignal,
): Promise<ExpenseListResponse> =>
  request<ExpenseListResponse>(
    `${base}?${expenseListQuery(params)}`,
    { signal },
  );

const json = async (): Promise<HeadersInit> =>
  defaultFetchHeaders("application/json");
const request = async <T>(url: string, init: RequestInit = {}) => {
  if (init.body instanceof FormData) {
    init.body.forEach((value) => {
      if (value instanceof File) validateExpenseUploadFile(value);
    });
  }
  const headers = init.body instanceof FormData
    ? await defaultFetchHeaders()
    : await json();
  if (init.body instanceof FormData) {
    delete (headers as Record<string, string>)["Content-Type"];
  }
  return fetchJson<T>(
    url,
    {
      ...init,
      credentials: "include",
      headers: {
        ...headers,
        ...(init.headers || {}),
      },
    },
    "Expense request failed",
  );
};

const attachmentFile = async (url: string, signal?: AbortSignal): Promise<Blob> => {
  const response = await fetch(url, {
    credentials: "include",
    headers: await defaultFetchHeaders(),
    signal,
  });
  if (!response.ok) throw new Error("Unable to load attachment.");
  return response.blob();
};

type MerchantMapPoint = { label: string; latitude: number; longitude: number };

export const expenseApi = {
  merchantMap: (expenseId: string, signal?: AbortSignal) => request<MerchantMapPoint>(`${base}/${encodeURIComponent(expenseId)}/merchant-map`, { signal }),
  list: listExpenses,
  projects: (params: Omit<ExpenseListParams, "sort"> & { sort?: string; includeInactive?: boolean } = {}, signal?: AbortSignal) =>
    request<{ items: ExpenseProjectSummary[]; nextCursor: string | null }>(`${base}/projects${dateQuery(params)}`, { signal }),
  projectOptions: (params: { search?: string; cursor?: string; exactName?: boolean } = {}, signal?: AbortSignal) =>
    request<{ items: ExpenseProjectOption[]; nextCursor: string | null }>(`${base}/projects/options${dateQuery(params)}`, { signal }),
  projectOption: (id: string, signal?: AbortSignal) => request<{ items: ExpenseProjectOption[]; nextCursor: string | null }>(`${base}/projects/options/${encodeURIComponent(id)}`, { signal }),
  project: (id: string, signal?: AbortSignal) => request<ExpenseProject>(`${base}/projects/${encodeURIComponent(id)}`, { signal }),
  createProject: (payload: { name: string; notes: string }, signal?: AbortSignal) => request<ExpenseProject>(`${base}/projects`, { method: "POST", body: JSON.stringify(payload), signal }),
  updateProject: (id: string, payload: { name: string; notes?: string; active: boolean }, signal?: AbortSignal) => request<ExpenseProject>(`${base}/projects/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(payload), signal }),
  updateProjectNotes: (id: string, notes: string, signal?: AbortSignal) => request<ExpenseProject>(`${base}/projects/${encodeURIComponent(id)}/notes`, { method: "PATCH", body: JSON.stringify({ notes }), signal }),
  summary: () => request<ExpenseSummary>(`${base}/summary`),
  reportByCategory: (params: ExpenseDateRange = {}, signal?: AbortSignal) =>
    request<ExpenseCategoryReport[]>(
      `${base}/reports/by-category${dateQuery(params)}`,
      { signal },
    ),
  reportByPeriod: (params: ExpenseDateRange & ExpenseReportScope & { profitLoss?: boolean } = {}, signal?: AbortSignal) =>
    request<ExpensePeriodReport[]>(
      `${base}/reports/by-period${dateQuery(params)}`,
      { signal },
    ),
  reportByPaymentAccount: (
    params: ExpenseDateRange = {},
    signal?: AbortSignal,
  ) =>
    request<ExpensePaymentAccountReport[]>(
      `${base}/reports/by-payment-account${dateQuery(params)}`,
      { signal },
    ),
  reportByMerchant: (params: ExpenseListParams = {}, signal?: AbortSignal) =>
    request<ExpenseMerchantReportPage>(
      `${base}/reports/by-merchant?${expenseListQuery(params)}`,
      { signal },
    ),
  merchantSuggestion: (query: string, signal?: AbortSignal) =>
    request<ExpenseMerchantSuggestionResponse>(
      `${base}/merchants/suggestion?${new URLSearchParams({ query })}`,
      { signal },
    ),
  merchantProfileMap: (merchantKey: string, signal?: AbortSignal) =>
    request<MerchantMapPoint>(
      `${base}/merchants/map?${new URLSearchParams({ merchantKey })}`,
      { signal },
    ),
  merchantDetail: (merchantKey: string, signal?: AbortSignal) =>
    request<ExpenseMerchantDetails>(
      `${base}/merchants/detail?${new URLSearchParams({ merchantKey })}`,
      { signal },
    ),
  updateMerchant: (merchantKey: string, payload: Pick<ExpenseMerchantDetails, "merchantAddress" | "storeNumber">) =>
    request<ExpenseMerchantDetails>(
      `${base}/merchants/detail?${new URLSearchParams({ merchantKey })}`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
  detail: (id: string, signal?: AbortSignal) =>
    request<Expense>(`${base}/${encodeURIComponent(id)}`, { signal }),
  categories: (includeInactive = false, signal?: AbortSignal) =>
    request<ExpenseCategory[]>(
      `${base}/categories${includeInactive ? "?includeInactive=true" : ""}`,
      { signal },
    ),
  category: (id: string) =>
    request<ExpenseCategory>(`${base}/categories/${encodeURIComponent(id)}`),
  createCategory: (payload: ExpenseCategoryCreatePayload) =>
    request<ExpenseCategory>(`${base}/categories`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateCategory: (id: string, payload: ExpenseCategoryUpdatePayload) =>
    request<ExpenseCategory>(`${base}/categories/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  paymentAccounts: (includeInactive = false, signal?: AbortSignal) =>
    request<PaymentAccount[]>(
      `${base}/payment-accounts${includeInactive ? "?includeInactive=true" : ""}`,
      { signal },
    ),
  paymentAccount: (id: string) =>
    request<PaymentAccount>(
      `${base}/payment-accounts/${encodeURIComponent(id)}`,
    ),
  createPaymentAccount: (payload: PaymentAccountCreatePayload) =>
    request<PaymentAccount>(`${base}/payment-accounts`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updatePaymentAccount: (id: string, payload: PaymentAccountUpdatePayload) =>
    request<PaymentAccount>(
      `${base}/payment-accounts/${encodeURIComponent(id)}`,
      { method: "PUT", body: JSON.stringify(payload) },
    ),
  schedules: (
    params: ExpenseDateRange & { allTime?: boolean } = {},
    signal?: AbortSignal,
  ) =>
    request<ExpenseSchedule[]>(`${base}/schedules${dateQuery(params)}`, {
      signal,
    }),
  schedule: (id: string) =>
    request<ExpenseSchedule>(`${base}/schedules/${encodeURIComponent(id)}`),
  scheduleLinkOptions: (search = "", signal?: AbortSignal) =>
    request<ExpenseScheduleLinkOption[]>(
      `${base}/schedule-link-options?search=${encodeURIComponent(search)}&limit=25`,
      { signal },
    ),
  previewScheduleLink: (payload: ExpenseScheduleLinkPreviewPayload) =>
    request<ExpenseScheduleLinkPreview>(`${base}/schedule-link-preview`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  createSchedule: (payload: ExpenseScheduleCreatePayload) =>
    request<ExpenseSchedule>(`${base}/schedules`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateSchedule: (
    id: string,
    payload: ExpenseScheduleUpdatePayload,
    updateDraftExpenses = false,
  ) =>
    request<ExpenseSchedule>(`${base}/schedules/${encodeURIComponent(id)}${
      updateDraftExpenses ? "?updateDraftExpenses=true" : ""
    }`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  scheduleDraftUpdatePreview: (id: string) =>
    request<{ draftCount: number }>(
      `${base}/schedules/${encodeURIComponent(id)}/draft-update-preview`,
    ),
  activateSchedule: (id: string) =>
    request<ExpenseSchedule>(
      `${base}/schedules/${encodeURIComponent(id)}/activate`,
      { method: "POST" },
    ),
  deactivateSchedule: (id: string) =>
    request<ExpenseSchedule>(
      `${base}/schedules/${encodeURIComponent(id)}/deactivate`,
      { method: "POST" },
    ),
  retryScheduleGeneration: (id: string) =>
    request<ExpenseSchedule>(
      `${base}/schedules/${encodeURIComponent(id)}/retry-generation`,
      { method: "POST" },
    ),
  previewScheduleBackfill: (
    id: string,
    payload: ExpenseScheduleBackfillPayload,
  ) =>
    request<ExpenseScheduleBackfillPreview>(
      `${base}/schedules/${encodeURIComponent(id)}/backfill/preview`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
  backfillSchedule: (id: string, payload: ExpenseScheduleBackfillPayload) =>
    request<ExpenseScheduleBackfillResult>(
      `${base}/schedules/${encodeURIComponent(id)}/backfill`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
  create: (payload: ExpenseCreatePayload) =>
    request<Expense>(base, { method: "POST", body: JSON.stringify(payload) }),
  previewDuplicate: (payload: ExpenseCreatePayload, expenseId?: string) =>
    request<ExpenseDuplicatePreview>(`${base}/duplicate-preview`, {
      method: "POST",
      body: JSON.stringify({ ...payload, expenseId: expenseId || null }),
    }),
  update: (id: string, payload: ExpenseEditPayload) =>
    request<Expense>(`${base}/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  updateDescription: (id: string, description: string) =>
    request<Expense>(`${base}/${encodeURIComponent(id)}/description`, {
      method: "PATCH",
      body: JSON.stringify({ description }),
    }),
  createShipment: (expenseId: string, payload: ExpenseShipmentMutation) =>
    request<ExpenseShipment>(
      `${base}/${encodeURIComponent(expenseId)}/shipments`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
  updateShipment: (
    expenseId: string,
    shipmentId: string,
    payload: ExpenseShipmentMutation,
  ) =>
    request<ExpenseShipment>(
      `${base}/${encodeURIComponent(expenseId)}/shipments/${encodeURIComponent(shipmentId)}`,
      { method: "PUT", body: JSON.stringify(payload) },
    ),
  syncShipments: (
    expenseId: string,
    shipments: ExpenseShipmentMutationInput[],
    creationOperationId?: string,
  ) =>
    request<ExpenseShipment[]>(
      creationOperationId ? `${base}/${encodeURIComponent(expenseId)}/creation-shipments?operationId=${encodeURIComponent(creationOperationId)}` : `${base}/${encodeURIComponent(expenseId)}/shipments`,
      {
        method: creationOperationId ? "POST" : "PUT",
        body: JSON.stringify({ shipments }),
      },
    ),
  removeShipment: (expenseId: string, shipmentId: string) =>
    request<void>(
      `${base}/${encodeURIComponent(expenseId)}/shipments/${encodeURIComponent(shipmentId)}`,
      { method: "DELETE" },
    ),
  refreshShipment: (expenseId: string, shipmentId: string) =>
    request<ExpenseShipmentRefresh>(
      `${base}/${encodeURIComponent(expenseId)}/shipments/${encodeURIComponent(shipmentId)}/refresh`,
      { method: "POST" },
    ),
  restoreShipment: (
    expenseId: string,
    shipmentId: string,
    mode: "alongside" | "replace",
    replaceShipmentId?: string,
  ) =>
    request<ExpenseShipment>(
      `${base}/${encodeURIComponent(expenseId)}/shipments/${encodeURIComponent(shipmentId)}/restore`,
      {
        method: "POST",
        body: JSON.stringify({
          mode,
          replaceShipmentId: mode === "replace" ? replaceShipmentId : null,
        }),
      },
    ),
  confirm: (id: string, duplicateWarningFingerprint?: string) =>
    request<ExpenseConfirmationResult>(`${base}/${encodeURIComponent(id)}/confirm`, {
      method: "POST",
      body: JSON.stringify({
        acknowledgeDuplicateWarning: Boolean(duplicateWarningFingerprint),
        duplicateWarningFingerprint: duplicateWarningFingerprint || null,
      }),
    }),
  reject: (id: string) =>
    request<Expense>(`${base}/${encodeURIComponent(id)}/reject`, {
      method: "POST",
    }),
  void: (id: string) =>
    request<Expense>(`${base}/${encodeURIComponent(id)}/void`, {
      method: "POST",
    }),
  correct: (id: string) =>
    request<Expense>(`${base}/${encodeURIComponent(id)}/correct`, {
      method: "POST",
    }),
  uploadAttachment: (id: string, file: File, creationOperationId?: string) => {
    const body = new FormData();
    body.append("file", file);
    return request<ExpenseAttachment>(
      creationOperationId ? `${base}/${encodeURIComponent(id)}/creation-attachment?operationId=${encodeURIComponent(creationOperationId)}` : `${base}/${encodeURIComponent(id)}/attachment`,
      { method: "POST", body },
    );
  },
  replaceAttachment: (id: string, attachmentId: string, file: File) => {
    const body = new FormData();
    body.append("file", file);
    return request<ExpenseAttachment>(
      `${base}/${encodeURIComponent(id)}/attachment/${encodeURIComponent(attachmentId)}/replace`,
      { method: "POST", body },
    );
  },
  removeAttachment: (id: string, attachmentId: string) =>
    request<Expense>(
      `${base}/${encodeURIComponent(id)}/attachment/${encodeURIComponent(attachmentId)}`,
      { method: "DELETE" },
    ),
  extractAttachment: (id: string, attachmentId: string) =>
    request<ExpenseExtraction>(
      `${base}/${encodeURIComponent(id)}/attachment/${encodeURIComponent(attachmentId)}/extract`,
      { method: "POST" },
    ),
  attachmentExtractionReview: (id: string, attachmentId: string) =>
    request<ReceiptReview>(
      `${base}/${encodeURIComponent(id)}/attachment/${encodeURIComponent(attachmentId)}/extraction-review`,
    ),
  attachmentFile: (id: string, attachmentId: string, signal?: AbortSignal) =>
    attachmentFile(
      `${base}/${encodeURIComponent(id)}/attachment/${encodeURIComponent(attachmentId)}?preview=true`,
      signal,
    ),
  downloadAttachment: (id: string, attachmentId: string) =>
    attachmentFile(
      `${base}/${encodeURIComponent(id)}/attachment/${encodeURIComponent(attachmentId)}?preview=false`,
    ),
  createReceiptImport: (file: File) => {
    const body = new FormData();
    body.append("file", file);
    return request<ReceiptImport>(`${base}/receipt-import`, {
      method: "POST",
      body,
    });
  },
  receiptImport: (id: string) =>
    request<ReceiptImport>(`${base}/receipt-import/${encodeURIComponent(id)}`),
  receiptImportPreview: (id: string, signal?: AbortSignal) =>
    attachmentFile(
      `${base}/receipt-import/${encodeURIComponent(id)}/preview`,
      signal,
    ),
  retryReceiptImport: (id: string) =>
    request<ReceiptImport>(
      `${base}/receipt-import/${encodeURIComponent(id)}/retry`,
      { method: "POST" },
    ),
  cancelReceiptImport: (id: string) =>
    request<void>(`${base}/receipt-import/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  createImportBatch: () =>
    request<ExpenseImportBatch>(`${base}/import-batches`, {
      method: "POST",
    }),
  importBatch: (id: string, candidateOffset = 0, candidateLimit = 50) =>
    request<ExpenseImportBatch>(
      `${base}/import-batches/${encodeURIComponent(id)}?candidateOffset=${candidateOffset}&candidateLimit=${candidateLimit}`,
    ),
  uploadImportBatchFile: (id: string, file: File) => {
    const body = new FormData();
    body.append("file", file);
    return request<ExpenseImportSourceFile>(
      `${base}/import-batches/${encodeURIComponent(id)}/files`,
      { method: "POST", body },
    );
  },
  retryImportBatchFile: (batchId: string, sourceId: string) =>
    request<ExpenseImportSourceFile>(
      `${base}/import-batches/${encodeURIComponent(batchId)}/files/${encodeURIComponent(sourceId)}/retry`,
      { method: "POST" },
    ),
  removeImportBatchFile: (batchId: string, sourceId: string) =>
    request<void>(
      `${base}/import-batches/${encodeURIComponent(batchId)}/files/${encodeURIComponent(sourceId)}`,
      { method: "DELETE" },
    ),
  decideImportCandidate: (
    batchId: string,
    candidateId: string,
    decision:
      | { action: "create"; expense: ExpenseCreatePayload }
      | { action: "exclude"; reason: string }
      | { action: "duplicate"; duplicateExpenseId: string }
      | { action: "restore" },
  ) =>
    request<ExpenseImportCandidate>(
      `${base}/import-batches/${encodeURIComponent(batchId)}/candidates/${encodeURIComponent(candidateId)}`,
      { method: "POST", body: JSON.stringify(decision) },
    ),
  cancelImportBatch: (id: string) =>
    request<void>(`${base}/import-batches/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
};

export type ExpenseReportScope = {
  locationId?: string;
  machineId?: string;
  groupBy?: "location" | "machine";
};

export type ExpenseDateRange = {
  startDate?: string;
  endDate?: string;
  includeComparison?: boolean;
};
export type ExpenseMerchantSuggestion = {
  merchantKey: string;
  merchantName: string;
  expenseCount: number;
  merchantAddress?: MerchantAddress | null;
  storeNumber?: string;
};
export type ExpenseMerchantSuggestionResponse = {
  suggestion: ExpenseMerchantSuggestion | null;
};
export type ExpenseScheduleBackfillPayload = {
  fromDate: string;
  throughDate: string;
  previewToken?: string | null;
  occurrences?: Array<{
    expenseDate: string;
    status: "draft" | "confirmed" | "skip";
  }>;
};
export type ExpenseCategoryCreatePayload = {
  name: string;
  code: string;
};
export type ExpenseCategoryUpdatePayload = ExpenseCategoryCreatePayload & {
  active: boolean;
};
export type PaymentAccountCreatePayload = {
  label: string;
  type: PaymentAccountType;
  provider: string;
  walletProvider: string;
  brand: string;
  lastFour: string;
  additionalLastFours?: import("../../expense/types").PaymentAccountLastFour[];
};
export type PaymentAccountUpdatePayload = PaymentAccountCreatePayload & {
  active: boolean;
};
export type ExpenseListParams = ExpenseDateRange & {
  categoryIds?: string[];
  statuses?: ExpenseStatus[];
  paymentAccountIds?: string[];
  unassignedPaymentAccount?: boolean;
  locationId?: string;
  machineId?: string;
  productId?: string;
  scheduleIds?: string[];
  projectId?: string;
  merchantKey?: string;
  merchantSort?: `${"latest" | "count" | "awaiting_review" | "confirmed" | "total"}_${"asc" | "desc"}`;
  sort?: "status_asc" | "status_desc" | "date_asc" | "date_desc" | "location_asc" | "location_desc" | "amount_asc" | "amount_desc";
  search?: string;
  cursor?: string;
  limit?: number;
  includeTotal?: boolean;
};
export const expenseListQuery = (params: ExpenseListParams) =>
  new URLSearchParams(
    Object.entries(params)
      .filter(
        (entry) =>
          entry[1] !== undefined && entry[1] !== null && entry[1] !== "",
      )
      .map(([key, value]) => [key, Array.isArray(value) ? [...value].sort().join(",") : String(value)])
      .sort(([left], [right]) => left.localeCompare(right)),
  ).toString();
const dateQuery = (params: object) => {
  const query = new URLSearchParams(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null && value !== "")
      .map(([key, value]) => [key, String(value)]),
  );
  return query.toString() ? `?${query}` : "";
};

export type ExpenseCreatePayload = {
  operationId?: string;
  amountCents: number;
  currency?: string;
  expenseDate: string;
  merchantName?: string;
  merchantAddress?: {
    street: string; city: string; state: string; zip: string; country: string;
  } | null;
  merchantAddressRaw?: string;
  storeNumber?: string;
  receiptNumber?: string;
  subtotalCents?: number | null;
  shippingCents?: number | null;
  taxCents?: number | null;
  discountCents?: number | null;
  categoryId: string;
  projectId?: string | null;
  description?: string;
  paymentMethod?: string;
  paymentAccountId?: string | null;
  locationId?: string | null;
  machineId?: string | null;
  lineItems: ExpenseLineItemPayload[];
  recurringSchedule?: RecurringSchedulePayload | null;
  existingScheduleLink?: ExistingScheduleLinkPayload | null;
  receiptImportId?: string | null;
};
export type ExpenseDuplicatePreview = {
  duplicateWarning: DuplicateExpenseWarning | null;
};
export type ExpenseRecurringMutation = {
  action:
    | "unchanged"
    | "create"
    | "update"
    | "remove"
    | "link"
    | "change_link"
    | "unlink";
  schedule?: RecurringSchedulePayload | null;
  existingScheduleLink?: ExistingScheduleLinkPayload | null;
};
export type ExpenseEditPayload = Omit<
  ExpenseCreatePayload,
  "recurringSchedule"
> & {
  recurringMutation: ExpenseRecurringMutation;
};
export type RecurringSchedulePayload = {
  name: string;
  frequency: "weekly" | "monthly" | "quarterly" | "yearly" | "custom";
  interval: number;
  calculationType: "fixed";
  fixedAmountCents?: number | null;
  shippingCents?: number;
  rateBasisPoints?: number | null;
  lineItems: ExpenseLineItemPayload[];
};

export type ExistingScheduleLinkPayload = {
  scheduleId: string;
  occurrenceDate: string;
};

export type ExpenseScheduleLinkOption = {
  id: string;
  name: string;
  merchantName: string;
  calculationType: "fixed" | "provider";
  frequency: ExpenseSchedule["frequency"];
  interval: number;
  nextDueDate: string;
  currency: string;
  active: boolean;
};

export type ExpenseScheduleLinkPreviewPayload = {
  scheduleId: string;
  expenseDate: string;
  occurrenceDate?: string | null;
  amountCents: number;
  currency: string;
  expenseId?: string | null;
};

export type ExpenseScheduleLinkPreview = {
  scheduleId: string;
  scheduleName: string;
  occurrenceDate: string;
  suggestedOccurrences: Array<{
    occurrenceDate: string;
    occupied: boolean;
  }>;
  periodStart?: string | null;
  periodEnd?: string | null;
  expectedAmountCents?: number | null;
  actualAmountCents: number;
  differenceCents?: number | null;
  currency: string;
  eligible: boolean;
  occupied: boolean;
  occupiedExpenseId?: string | null;
  occupiedExpenseStatus?: string | null;
  warnings: Array<{
    code: string;
    message: string;
    severity: "warning" | "error";
    action: "schedule" | "expense";
  }>;
};

export type ExpenseScheduleRateComponentPayload = {
  type: ExpenseScheduleRateComponentType;
  rateBasisPoints?: number | null;
  amountCents?: number | null;
};

export type ExpenseScheduleRateGroupPayload = {
  name: string;
  roundingMode: ExpenseScheduleRoundingMode;
  machineIds: string[];
  components: ExpenseScheduleRateComponentPayload[];
};

type ExpenseScheduleBasePayload = {
  name: string;
  categoryId: string;
  merchantName: string;
  merchantAddress?: {
    street: string; city: string; state: string; zip: string; country: string;
  } | null;
  merchantAddressRaw?: string;
  storeNumber?: string;
  description: string;
  frequency: "weekly" | "monthly" | "quarterly" | "yearly" | "custom";
  interval: number;
  nextDueDate: string;
  currency: string;
  paymentMethod: string;
  paymentAccountId: string | null;
  locationId: string | null;
  machineId: string | null;
};

export type ExpenseScheduleCreatePayload =
  | (ExpenseScheduleBasePayload &
      RecurringSchedulePayload & {
        telemetryId?: null;
        rateGroups?: [];
      })
  | (ExpenseScheduleBasePayload & {
      calculationType: "provider";
      fixedAmountCents?: null;
      shippingCents?: 0;
      rateBasisPoints?: null;
      lineItems: [];
      telemetryId: string;
      rateGroups: ExpenseScheduleRateGroupPayload[];
    });

export type ExpenseScheduleUpdatePayload =
  Partial<ExpenseScheduleCreatePayload>;
export type ExpenseLineItemPayload = {
  inventoryId?: string | null;
  inventoryTitle?: string | null;
  inventoryQuantity?: number | string | null;
  productId?: string | null;
  productName?: string;
  editorKey?: string;
  itemName: string;
  quantity: number | string;
  unitPriceCents: number;
  lineAmountCents?: number | null;
  discountCents?: number | null;
  taxCents?: number | null;
  categoryId?: string | null;
  projectId?: string | null;
  projectName?: string;
  projectExcluded?: boolean;
  sortOrder?: number;
};
export type ExpenseShipmentMutation = {
  carrier: ExpenseShipmentCarrier;
  carrierName: string;
  trackingNumber: string;
  shippedAt: string | null;
  receivedAt: string | null;
};
export type ExpenseShipmentMutationInput = ExpenseShipmentMutation & {
  id?: string;
};
export type ExpenseShipmentRefresh = {
  shipmentId: string;
  queued: boolean;
  refreshAvailable: boolean;
  refreshAfter: string | null;
};
